import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, todayIso } from './dates';
import { must, useDb } from './db';

/**
 * «Загадка дня»: one short puzzle a day, the same for the whole school; part of the app, so it
 * opens offline too. Days in a row make a streak; seven make «Мозковий штурм».
 */
export interface Puzzle {
  text: string;
  options: string[];
  correct: number;
  explain: string;
}

export const PUZZLES: readonly Puzzle[] = [
  {
    text: 'Що можна зламати, навіть не торкаючись?',
    options: ['Склянку', 'Обіцянку', 'Олівець'],
    correct: 1,
    explain: 'Обіцянку ламають словом, а не руками.',
  },
  {
    text: 'Скільки місяців на рік мають 28 днів?',
    options: ['Лише лютий', 'Усі дванадцять', 'Жоден'],
    correct: 1,
    explain: 'У кожному місяці є 28-ме число.',
  },
  {
    text: 'Скільки буде половина від двох плюс два?',
    options: ['2', '3', '4'],
    correct: 1,
    explain: 'Половина від двох — 1, і ще 2 — разом 3.',
  },
  {
    text: 'Що стає більшим, коли з нього забирають?',
    options: ['Хмара', 'Яма', 'Річка'],
    correct: 1,
    explain: 'Чим більше землі забираєш, тим глибша яма.',
  },
  {
    text: 'Півень сидить на даху. Куди покотиться його яйце?',
    options: ['Ліворуч', 'Праворуч', 'Нікуди'],
    correct: 2,
    explain: 'Півні яєць не несуть.',
  },
  {
    text: 'Продовж ряд: 2, 6, 12, 20, 30, …',
    options: ['40', '42', '44'],
    correct: 1,
    explain: 'Різниця щоразу більша на 2: +4, +6, +8, +10, +12.',
  },
  {
    text: 'Що завжди попереду, але ніколи не настає?',
    options: ['Завтра', 'Поїзд', 'Дощ'],
    correct: 0,
    explain: 'Коли «завтра» настає, воно вже «сьогодні».',
  },
  {
    text: 'Яке слово зайве: яблуко, груша, морква, слива?',
    options: ['Груша', 'Морква', 'Слива'],
    correct: 1,
    explain: 'Морква — овоч, решта — фрукти.',
  },
  {
    text: 'У мами 4 доньки, і в кожної є брат. Скільки в мами дітей?',
    options: ['5', '8', '9'],
    correct: 0,
    explain: 'Брат у всіх доньок один і той самий.',
  },
  {
    text: 'Скільки разів можна відняти 5 від 25?',
    options: ['5', '1', 'Безліч'],
    correct: 1,
    explain: 'Після першого разу віднімаєш уже від 20.',
  },
  {
    text: 'Годинник показує 3:15. Який кут між стрілками?',
    options: ['0°', '7,5°', '15°'],
    correct: 1,
    explain: 'За чверть години годинна стрілка проходить чверть від 30°.',
  },
  {
    text: 'Яке число наступне: 1, 4, 9, 16, …',
    options: ['20', '25', '32'],
    correct: 1,
    explain: 'Це квадрати чисел: 1², 2², 3², 4², 5².',
  },
  {
    text: 'Горіло 5 свічок, 2 з них задули. Скільки свічок залишиться?',
    options: ['3', '2', '5'],
    correct: 1,
    explain: 'Ті, що горіли далі, згорять, а задуті залишаться.',
  },
  {
    text: 'Що можна побачити із заплющеними очима?',
    options: ['Сон', 'Небо', 'Нічого'],
    correct: 0,
    explain: 'Сни бачимо саме тоді.',
  },
  {
    text: 'Скільки ніг у павука?',
    options: ['6', '8', '10'],
    correct: 1,
    explain: 'Павуки — не комахи: у них вісім ніг.',
  },
  {
    text: 'Яке місто називають «містом Лева»?',
    options: ['Луцьк', 'Львів', 'Харків'],
    correct: 1,
    explain: 'Лев — символ Львова, місто назвали на честь князя Лева.',
  },
  {
    text: 'Сьогодні вівторок. Який день буде через 10 днів?',
    options: ['Четвер', 'Пʼятниця', 'Субота'],
    correct: 1,
    explain: '7 днів — знову вівторок, ще 3 — пʼятниця.',
  },
  {
    text: 'Скільки секунд в одній годині?',
    options: ['360', '3600', '6000'],
    correct: 1,
    explain: '60 хвилин × 60 секунд.',
  },
  {
    text: 'Батьку й синові разом 36 років, батько старший на 30. Скільки синові?',
    options: ['3', '6', '4'],
    correct: 0,
    explain: '3 + 33 = 36, а різниця — 30.',
  },
  {
    text: 'Що розбивається, щойно його назвеш?',
    options: ['Скло', 'Тиша', 'Секрет'],
    correct: 1,
    explain: 'Скажеш слово — і тиші вже немає.',
  },
  {
    text: 'Скільки буде 0,1 × 0,1?',
    options: ['0,1', '0,01', '1'],
    correct: 1,
    explain: 'Десята частина від десятої — сота.',
  },
];

const dayNumber = (iso: string) => Math.floor(Date.parse(`${iso}T12:00:00Z`) / 86_400_000);

export const puzzleOfDay = (iso: string): Puzzle =>
  PUZZLES[((dayNumber(iso) % PUZZLES.length) + PUZZLES.length) % PUZZLES.length] as Puzzle;

/** Days in a row with an answer, up to today (or up to yesterday while today is still open). */
export function puzzleStreak(days: ReadonlyArray<string>, today: string): number {
  const answered = new Set(days);
  let day = answered.has(today) ? today : addDays(today, -1);
  let streak = 0;
  while (answered.has(day)) {
    streak += 1;
    day = addDays(day, -1);
  }
  return streak;
}

export const BRAINSTORM_DAYS = 7;

/** The student's answers of the last weeks (their own only). */
export function usePuzzleAnswers(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['puzzle', studentId],
    queryFn: async () =>
      must(
        await db
          .from('puzzle_answers')
          .select('day, correct')
          .eq('student_member_id', studentId)
          .gte('day', addDays(todayIso(), -60)),
      ),
  });
}

export function useAnswerPuzzle() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { schoolId: string; studentId: string; correct: boolean }) =>
      must(
        await db
          .from('puzzle_answers')
          .upsert(
            { school_id: a.schoolId, student_member_id: a.studentId, correct: a.correct },
            { onConflict: 'student_member_id,day', ignoreDuplicates: true },
          ),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['puzzle'] }),
  });
}
